import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import Footer from './Footer';
import { useToast } from './Toast';
import { checklistPages } from '../data/checklistConfig';

const AUTOSAVE_KEY = 'checklistDraft';

function ChecklistItem({ label, description, statusText, value, onSetOk, onSetFail }) {
  const statusClass = value === true ? 'status-ok' : value === false ? 'status-fail' : '';
  return (
    <div className={`check-item ${statusClass}`}>
      <div className="check-item-header">
        <span className="check-item-label">{label}</span>
      </div>
      <p className="check-item-desc">{description}</p>
      <div className="check-item-buttons">
        <button
          type="button"
          className={`check-btn ${value === true ? 'ok-selected' : ''}`}
          onClick={onSetOk}
          aria-label={`${label}: OK`}
        >
          ✓ OK
        </button>
        <button
          type="button"
          className={`check-btn ${value === false ? 'fail-selected' : ''}`}
          onClick={onSetFail}
          aria-label={`${label}: ${statusText}`}
        >
          ✕ {statusText}
        </button>
      </div>
    </div>
  );
}

function ChecklistForm({ dominio, user, showFluidos }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [profileData, setProfileData] = useState({ nombre: '', empresa: '' });
  const [checklistData, setChecklistData] = useState({
    kilometraje: '', documentacion: null, combustible: '',
    lucesCriticas: null, lucesGenerales: null, perimetro: null,
    seguridadInterior: null, tableroCabina: null,
    presurizados: null, comunicacion: null,
    fluidos: null, herramientas: null, kitEmergencia: null,
    observaciones: '',
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [validationErrors, setValidationErrors] = useState({});

  const filteredPages = checklistPages.filter(page =>
    page.title !== 'Fluidos y Herramientas' || showFluidos
  );

  // Online/offline listener
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Load user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      try {
        const docSnap = await getDoc(doc(db, 'users', user.uid));
        if (docSnap.exists()) {
          setProfileData({
            nombre: docSnap.data().nombre || '',
            empresa: docSnap.data().empresa || ''
          });
        }
      } catch (e) { console.error("Error cargando perfil:", e); }
    };
    fetchProfile();
  }, [user]);

  // Autosave: restore draft
  useEffect(() => {
    try {
      const draft = localStorage.getItem(AUTOSAVE_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed.dominio === dominio.toUpperCase()) {
          setChecklistData(prev => ({ ...prev, ...parsed.checklistData }));
          setProfileData(prev => ({ ...prev, ...parsed.profileData }));
          setStep(parsed.step || 0);
          toast.info("Se restauró un borrador anterior.");
        }
      }
    } catch { /* ignore */ }
  }, [dominio, toast]);

  // Autosave: save on page change
  useEffect(() => {
    const draft = { dominio: dominio.toUpperCase(), checklistData, profileData, step };
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(draft));
  }, [step, checklistData, profileData, dominio]);

  // Cleanup preview URLs
  useEffect(() => {
    return () => previewUrls.forEach(url => URL.revokeObjectURL(url));
  }, [previewUrls]);

  const setField = (field, value) => {
    setChecklistData(prev => ({ ...prev, [field]: value }));
    setValidationErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  };

  const validateCurrentPage = () => {
    const errors = {};
    const page = filteredPages[step];
    if (!page) return true;

    for (const field of page.fields) {
      if (field.type === 'profile_name' && !profileData.nombre.trim()) {
        errors.nombre = 'Requerido';
      }
      if (field.type === 'profile_company' && !profileData.empresa.trim()) {
        errors.empresa = 'Requerido';
      }
      if (field.type === 'kilometraje' && (!checklistData.kilometraje || Number(checklistData.kilometraje) <= 0)) {
        errors.kilometraje = 'Ingresá el kilometraje';
      }
      if (field.type === 'combustible' && !checklistData.combustible) {
        errors.combustible = 'Seleccioná un nivel';
      }
      if (!field.type && checklistData[field.id] === null) {
        errors[field.id] = 'Seleccioná OK o indicá la falla';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    if (files.length === 0) { setImageFiles([]); setPreviewUrls([]); return; }

    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(newPreviews);

    toast.info(`Comprimiendo ${files.length} imagen(es)...`);
    const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };

    try {
      const compressed = await Promise.all(files.map(f => imageCompression(f, options)));
      toast.success('Imágenes procesadas.');
      setImageFiles(compressed);
    } catch {
      toast.warning('Error al comprimir. Se usarán los archivos originales.');
      setImageFiles(files);
    }
  };

  const handleSave = async () => {
    if (!validateCurrentPage()) {
      toast.warning('Completá todos los campos antes de guardar.');
      return;
    }

    setIsSaving(true);
    try {
      const isCurrentlyOffline = !navigator.onLine;

      if (!isCurrentlyOffline) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          await setDoc(userDocRef, { nombre: profileData.nombre, empresa: profileData.empresa }, { merge: true });
        } catch (e) { console.warn("No se pudo actualizar perfil:", e); }
      }

      let imageUrls = [];
      let offlineMessage = null;

      if (!isCurrentlyOffline && imageFiles.length > 0) {
        const uploads = imageFiles.map(file => {
          const fileRef = ref(storage, `checklists/${uuidv4()}-${file.name}`);
          return uploadBytes(fileRef, file).then(snap => getDownloadURL(snap.ref));
        });
        imageUrls = await Promise.all(uploads);
      } else if (isCurrentlyOffline && imageFiles.length > 0) {
        offlineMessage = "Las imágenes no se guardaron y deberán adjuntarse más tarde.";
      }

      const dataToSave = {
        ...checklistData,
        fotos: imageUrls,
        dominio: dominio.toUpperCase(),
        userEmail: user.email,
        userName: profileData.nombre,
        userCompany: profileData.empresa,
        esCondicional: false,
        fluidsWasChecked: showFluidos,
        fechaRealCreacion: new Date().toISOString(),
        guardadoOffline: isCurrentlyOffline
      };

      const visibleFieldIds = filteredPages.flatMap(p => p.fields.map(f => f.id));
      const isConditional = visibleFieldIds.some(
        fid => typeof checklistData[fid] === 'boolean' && checklistData[fid] === false
      );
      dataToSave.esCondicional = isConditional;

      const pendingItemsList = [];
      if (isConditional) {
        visibleFieldIds.forEach(fid => {
          const pageInfo = checklistPages.find(p => p.fields.some(f => f.id === fid));
          const fieldInfo = pageInfo?.fields.find(f => f.id === fid);
          if (fieldInfo && checklistData[fid] === false) {
            pendingItemsList.push({ id: fid, label: fieldInfo.label });
          }
        });
      }

      localStorage.removeItem(AUTOSAVE_KEY);

      navigate('/confirmacion-condicional', {
        state: {
          dataToSave,
          pendingItems: pendingItemsList,
          isConditional,
          offlineInfo: offlineMessage
        }
      });
    } catch (e) {
      console.error("Error al preparar datos:", e);
      toast.error("Error al preparar el checklist.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    if (validateCurrentPage()) {
      setStep(prev => Math.min(prev + 1, filteredPages.length - 1));
    }
  };
  const handlePrev = () => setStep(prev => Math.max(prev - 1, 0));

  if (!filteredPages[step]) return <div className="loading-screen"><div className="spinner spinner-lg"></div></div>;
  const currentPage = filteredPages[step];
  const progress = ((step + 1) / filteredPages.length) * 100;

  return (
    <div className="checklist-container">
      {!isOnline && (
        <div className="offline-banner" role="status">
          <span aria-hidden="true">⚡</span> Modo sin conexión
        </div>
      )}

      <div className="checklist-header">
        <h2>{currentPage.title}</h2>
        <div className="checklist-progress">
          <span>Paso {step + 1} de {filteredPages.length}</span>
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      </div>

      <div className="checklist-body">
        {currentPage.fields.map(field => {
          if (field.type === 'profile_name') return (
            <div key={field.id} className="form-group">
              <label htmlFor="cl-nombre" className="form-label">{field.label}</label>
              <input
                id="cl-nombre" type="text"
                className={`form-input ${validationErrors.nombre ? 'error' : ''}`}
                value={profileData.nombre}
                onChange={(e) => { setProfileData({ ...profileData, nombre: e.target.value }); setValidationErrors(prev => { const n = { ...prev }; delete n.nombre; return n; }); }}
              />
              {validationErrors.nombre && <p className="form-error">{validationErrors.nombre}</p>}
            </div>
          );

          if (field.type === 'profile_company') return (
            <div key={field.id} className="form-group">
              <label htmlFor="cl-empresa" className="form-label">{field.label}</label>
              <input
                id="cl-empresa" type="text"
                className={`form-input ${validationErrors.empresa ? 'error' : ''}`}
                value={profileData.empresa}
                onChange={(e) => { setProfileData({ ...profileData, empresa: e.target.value }); setValidationErrors(prev => { const n = { ...prev }; delete n.empresa; return n; }); }}
              />
              {validationErrors.empresa && <p className="form-error">{validationErrors.empresa}</p>}
            </div>
          );

          if (field.type === 'kilometraje') return (
            <div key={field.id} className="form-group">
              <label htmlFor="cl-km" className="form-label">{field.label}</label>
              <input
                id="cl-km" type="number" min="1"
                className={`form-input ${validationErrors.kilometraje ? 'error' : ''}`}
                placeholder="Ej: 45230"
                value={checklistData.kilometraje}
                onChange={(e) => setField('kilometraje', e.target.value)}
              />
              {validationErrors.kilometraje && <p className="form-error">{validationErrors.kilometraje}</p>}
            </div>
          );

          if (field.type === 'combustible') return (
            <div key={field.id} className="form-group">
              <label htmlFor="cl-fuel" className="form-label">{field.label}</label>
              <select
                id="cl-fuel"
                className={`form-select ${validationErrors.combustible ? 'error' : ''}`}
                value={checklistData.combustible}
                onChange={(e) => setField('combustible', e.target.value)}
              >
                <option value="">Seleccionar nivel...</option>
                <option value="1/4">1/4 Tanque</option>
                <option value="1/2">1/2 Tanque</option>
                <option value="3/4">3/4 Tanque</option>
                <option value="Lleno">Lleno</option>
              </select>
              {validationErrors.combustible && <p className="form-error">{validationErrors.combustible}</p>}
            </div>
          );

          if (field.type === 'textarea') return (
            <div key={field.id} className="form-group">
              <label htmlFor="cl-obs" className="form-label">Observaciones</label>
              <textarea
                id="cl-obs"
                className="form-input"
                placeholder="Ingresá comentarios o detalles relevantes..."
                value={checklistData.observaciones}
                onChange={(e) => setField('observaciones', e.target.value)}
                rows="5"
              />
            </div>
          );

          if (field.type === 'file') return (
            <div key={field.id} className="form-group">
              <label htmlFor="cl-fotos" className="form-label">Fotos (máx. 3)</label>
              <input
                id="cl-fotos" type="file" multiple accept="image/*"
                className="form-input"
                onChange={handleFileChange}
              />
              {previewUrls.length > 0 && (
                <div className="preview-grid">
                  {previewUrls.map((url, i) => (
                    <img key={i} src={url} alt={`Vista previa ${i + 1}`} className="preview-thumb" />
                  ))}
                </div>
              )}
            </div>
          );

          return (
            <div key={field.id}>
              <ChecklistItem
                label={field.label}
                description={field.description}
                statusText={field.status}
                value={checklistData[field.id]}
                onSetOk={() => setField(field.id, true)}
                onSetFail={() => setField(field.id, false)}
              />
              {validationErrors[field.id] && <p className="form-error" style={{ marginTop: '-8px', marginBottom: '12px' }}>{validationErrors[field.id]}</p>}
            </div>
          );
        })}
      </div>

      <div className="checklist-nav">
        <button onClick={handlePrev} disabled={step === 0} className="nav-arrow" aria-label="Página anterior">
          ←
        </button>
        <span className="step-counter">{step + 1} / {filteredPages.length}</span>
        {step === filteredPages.length - 1 ? (
          <button onClick={handleSave} disabled={isSaving} className="btn btn-accent btn-lg">
            {isSaving ? 'Preparando...' : 'Guardar'}
          </button>
        ) : (
          <button onClick={handleNext} className="nav-arrow" aria-label="Página siguiente">
            →
          </button>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default ChecklistForm;
